import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Inbox, Mail, RefreshCw, MessageSquare, Search, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [pageFilter, setPageFilter] = useState("all");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/feedback");
      setItems(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setItems([]);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const pages = useMemo(() => {
    const set = new Set((items || []).map((f) => f.page).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items.filter((f) => {
      if (pageFilter !== "all" && (f.page || "") !== pageFilter) return false;
      if (!q) return true;
      return (
        (f.message || "").toLowerCase().includes(q) ||
        (f.email || "").toLowerCase().includes(q) ||
        (f.name || "").toLowerCase().includes(q) ||
        (f.page || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, pageFilter]);

  const hasFilters = query.trim() !== "" || pageFilter !== "all";

  if (user === undefined) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10" data-testid="admin-feedback-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6E6860] mb-2">Admin</p>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2D2A26] flex items-center gap-3">
            <Inbox className="h-7 w-7 text-[#4A5D4E]" strokeWidth={1.5} /> Feedback Inbox
          </h1>
          {items && (
            <p className="text-[#6E6860] mt-2 text-sm" data-testid="feedback-count">
              {hasFilters
                ? `${filtered.length} of ${items.length} ${items.length === 1 ? "note" : "notes"}`
                : `${items.length} ${items.length === 1 ? "note" : "notes"} received`}
            </p>
          )}
        </div>
        <button
          onClick={load}
          data-testid="feedback-refresh-button"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-[#4A5D4E]/30 text-[#4A5D4E] hover:bg-[#F5F2EA] transition-all"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {items && items.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="feedback-filters">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A69F94]" strokeWidth={1.75} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by keyword, email, or name…"
              data-testid="feedback-search-input"
              className="w-full rounded-full border border-[#E8E3D9] bg-white pl-10 pr-9 py-2.5 text-sm text-[#2D2A26] placeholder:text-[#A69F94] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/40"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="feedback-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-[#6E6860] hover:bg-[#F5F2EA]"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <select
            value={pageFilter}
            onChange={(e) => setPageFilter(e.target.value)}
            data-testid="feedback-page-filter"
            className="rounded-full border border-[#E8E3D9] bg-white px-4 py-2.5 text-sm text-[#2D2A26] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/40 sm:w-56"
          >
            <option value="all">All pages</option>
            {pages.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-[#B27A70]/40 bg-[#B27A70]/10 p-4 text-sm text-[#8a4a3f] mb-6" data-testid="feedback-error">
          {error}
        </div>
      )}

      {items === null ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-white border border-[#E8E3D9] animate-breathe" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      ) : items.length === 0 && !error ? (
        <div className="text-center py-20 rounded-2xl bg-white border border-[#E8E3D9]" data-testid="feedback-empty">
          <MessageSquare className="h-10 w-10 mx-auto text-[#C4A265] mb-3" strokeWidth={1.5} />
          <p className="font-serif text-xl text-[#2D2A26]">No feedback yet</p>
          <p className="text-sm text-[#6E6860] mt-1">Notes submitted through the app will appear here.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white border border-[#E8E3D9]" data-testid="feedback-no-matches">
          <Search className="h-10 w-10 mx-auto text-[#C4A265] mb-3" strokeWidth={1.5} />
          <p className="font-serif text-xl text-[#2D2A26]">No matching notes</p>
          <p className="text-sm text-[#6E6860] mt-1">Try a different keyword or clear your filters.</p>
          <button
            onClick={() => { setQuery(""); setPageFilter("all"); }}
            data-testid="feedback-clear-filters"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-[#4A5D4E]/30 text-[#4A5D4E] hover:bg-[#F5F2EA] transition-all"
          >
            <X className="h-4 w-4" /> Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="feedback-list">
          {filtered.map((f) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl bg-white border border-[#E8E3D9] p-5 sm:p-6 shadow-sm"
              data-testid={`feedback-item-${f.id}`}
            >
              <p className="text-[#2D2A26] leading-relaxed whitespace-pre-wrap mb-4">{f.message}</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[#6E6860] pt-3 border-t border-[#E8E3D9]">
                <span>{formatDate(f.created_at)}</span>
                {f.name && <span>· {f.name}</span>}
                {f.email ? (
                  <a
                    href={`mailto:${f.email}?subject=Re:%20your%20Common%20Ground%20Explorer%20feedback`}
                    className="inline-flex items-center gap-1.5 text-[#4A5D4E] hover:text-[#3B4A3E] underline underline-offset-4"
                    data-testid={`feedback-reply-${f.id}`}
                  >
                    <Mail className="h-3.5 w-3.5" /> {f.email}
                  </a>
                ) : (
                  <span className="italic">no email provided</span>
                )}
                {f.page && <span className="ml-auto text-[#A69F94]">from {f.page}</span>}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
